import * as React from "react";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { FormComponent } from "../../../../../../../react-core/Html/FormComponent";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { EligibilityStatus } from "../../../../../../../Wrap/Constants/Enums/Domain/EligibilityStatus";
import { AccountRoleByActorDetails } from "../../../../../../../Wrap/Models/Domains/Account/AccountRoleByActorDetails";
import { RegulationType } from "../../../../../../../Wrap/Constants/Enums/Domain/RegulationType";
import { CustomerCompliance } from "../../../../../../../Wrap/Models/Domains/Regulations/CustomerCompliance";
import { CountryCodeMap } from "../../../../../../Shared/Entities/Maps/CountryCodeMap";
import { Table } from "../../../../../../Shared/Table/Table";
import { AccountInfoAction } from "../../../PortfolioDashboard/Components/Role/Action/AccountInfoAction";
import {
    EligibilityStatusMap,
    EligibilityStatusOptions,
} from "../../../PortfolioDashboard/Components/Role/Resource/Contents";
import { ClientRecordAction } from "../../Action/ClientRecordAction";
import { Lightbox, LightboxType } from "../../../../../../../react-core/Html/Components/Lightbox";
import { SellAction } from "../../../Sell/Action/SellAction";
import { Contents } from "../../../PortfolioDashboard/Components/Transactions/Resource/Contents";
import { RoleType } from "../../../../../../../Wrap/Constants/Enums/RoleType";

interface ITaxComplianceAgreementProps {
    customerId: number;
    hasReadWriteAccess: boolean;
    customerComplianceList: Array<CustomerCompliance>;
    loadCompleted: () => void;
    firstLoad: boolean;
}

interface ITaxComplianceAgreementState {
    readOnly: boolean;
    customerComplianceList: Array<CustomerCompliance>;
    showLightbox: boolean;
    accountRoles: Array<AccountRoleByActorDetails>;
    customerName: string;
}

export class TaxComplianceAgreement extends FormComponent<ITaxComplianceAgreementProps, ITaxComplianceAgreementState> {
    constructor(props: any) {
        super(props);
        this.state = {
            readOnly: true,
            customerComplianceList: [],
            showLightbox: false,
            accountRoles: [],
            customerName: "",
        };
    }

    private table;
    componentDidMount() {
        this.getData();
    }
    componentDidUpdate(prevProps: any): void {
        if (!this.props.firstLoad && this.props.customerComplianceList != this.state.customerComplianceList) {
            this.setState({ customerComplianceList: this.props.customerComplianceList });
            this.props.loadCompleted();
        }
    }

    private getData() {
        AccountInfoAction.getCustomerRegulationCompliance(this.props.customerId, (result) => {
            this.setState({ customerComplianceList: result });
        });
        ClientRecordAction.getAccountRoleDetail(
            this.props.customerId.toString(),
            (results: Array<AccountRoleByActorDetails>) => {
                this.setState({ accountRoles: results });
            }
        );
        this.getCustomerName(this.props.customerId);
    }
    private getRegulationDescription(regulation: RegulationType) {
        switch (regulation) {
            case RegulationType.UKFatca:
                return "FATCA - UK";
            case RegulationType.Fatca:
                return "FATCA - USA";
            default:
                return `CRS - ${CountryCodeMap.getString(CountryCodeMap.getEnumByCountryCodeId(regulation))}`;
        }
    }

    private getCustomerName(customerId: number) {
        SellAction.getCustomerName(customerId, (result) => {
            this.setState({ customerName: result });
        });
    }

    public render(): JSX.Element {
        const { readOnly, customerComplianceList, customerName } = this.state;

        return (
            <>
                <h3>Tax Compliance Agreements</h3>
                <Table
                    data={customerComplianceList}
                    onRef={(ref) => (this.table = ref)}
                    cssWrapper="table-first-last-cell-padding-reset"
                    col={[
                        {
                            title: "Regulation",
                            key: "Regulation",
                            render: (data: string, row: CustomerCompliance, index) => {
                                return this.getRegulationDescription(row.RegulationName);
                            },
                        },
                        {
                            title: "Reportable Customer?",
                            key: "Reportable",
                            render: (data: string, row: CustomerCompliance, index) => {
                                return customerName;
                            },
                        },
                        {
                            title: "Indicia Status",
                            key: "EligibilityStatus",
                            render: (data, row: CustomerCompliance, index) => {
                                if (readOnly) {
                                    if (row.EligibilityStatus == EligibilityStatus.NotApplicable) {
                                        return "N/A";
                                    }
                                    return EligibilityStatusMap[row.EligibilityStatus];
                                } else {
                                    return (
                                        <div className="col-12 col-sm-fluid col-max-245">
                                            <SelectEditor
                                                key="EligibilityStatusOptions"
                                                selected={
                                                    EligibilityStatusOptions.filter(
                                                        (x) => x.key == row.EligibilityStatus
                                                    )[0].key
                                                }
                                                options={EligibilityStatusOptions.map((item) => item.key)}
                                                getText={(x) => CommonHelper.getOptionsTxt(x, EligibilityStatusOptions)}
                                                onChange={(val) => {
                                                    customerComplianceList[index].EligibilityStatus = val;
                                                    this.setState({ customerComplianceList });
                                                }}
                                            />
                                        </div>
                                    );
                                }
                            },
                        },
                        {
                            title: "Date First Contacted",
                            key: "DateFirstContacted",
                            render: (data, row: CustomerCompliance, index) => {
                                if (readOnly) {
                                    return row.DateFirstContacted
                                        ? JsonDateParser.getFormatDateString(
                                              JsonDateParser.toDate(row.DateFirstContacted),
                                              "dd-MMM-yyyy",
                                              true,
                                              true
                                          )
                                        : null;
                                } else {
                                    return (
                                        <DatePicker
                                            cssWrapper={"mb-0"}
                                            labelSRSufix="dd/mm/yyyy"
                                            selected={JsonDateParser.toDateNoOffset(row.DateFirstContacted)}
                                            showYearDropdown={true}
                                            alwaysValidateOnUpdate={true}
                                            onChange={(date) => {
                                                customerComplianceList[
                                                    index
                                                ].DateFirstContacted = JsonDateParser.toJsonDateNoOffset(date);
                                                this.setState({
                                                    customerComplianceList: customerComplianceList,
                                                });
                                            }}
                                            todayButton="Today"
                                            isClearable={false}
                                            dateFormat={"dd/MM/yyyy"}
                                            acceptDateFormats={[
                                                "dd/MM/yyyy",
                                                "dd-MM-yyyy",
                                                "ddMMyyyy",
                                                "dd.MM.yyyy",
                                                "dd_MM_yyyy",
                                                "dd MM yyyy",
                                            ]}
                                            lessThan={{
                                                value: new Date(),
                                                errorMessage: "Date of contact cannot be in the future",
                                            }}
                                            acceptDateFormatsErrorMsg={Contents.AcceptDateFormatsErrorMsg}
                                        />
                                    );
                                }
                            },
                        },
                        {
                            title: "Date Last Contacted",
                            key: "DateLastContacted",
                            render: (data, row: CustomerCompliance, index) => {
                                if (readOnly) {
                                    return row.DateLastContacted
                                        ? JsonDateParser.getFormatDateString(
                                              JsonDateParser.toDate(row.DateLastContacted),
                                              "dd-MMM-yyyy",
                                              true,
                                              true
                                          )
                                        : null;
                                } else {
                                    return (
                                        <DatePicker
                                            cssWrapper={"mb-0"}
                                            labelSRSufix="dd/mm/yyyy"
                                            selected={JsonDateParser.toDateNoOffset(row.DateLastContacted)}
                                            showYearDropdown={true}
                                            alwaysValidateOnUpdate={true}
                                            onChange={(date) => {
                                                customerComplianceList[
                                                    index
                                                ].DateLastContacted = JsonDateParser.toJsonDateNoOffset(date);

                                                this.setState({
                                                    customerComplianceList: customerComplianceList,
                                                });
                                            }}
                                            todayButton="Today"
                                            isClearable={false}
                                            dateFormat={"dd/MM/yyyy"}
                                            acceptDateFormats={[
                                                "dd/MM/yyyy",
                                                "dd-MM-yyyy",
                                                "ddMMyyyy",
                                                "dd.MM.yyyy",
                                                "dd_MM_yyyy",
                                                "dd MM yyyy",
                                            ]}
                                            atLeast={{
                                                value: JsonDateParser.toDateNoOffset(row.DateFirstContacted),
                                                errorMessage:
                                                    "Date of last contact must be the same day or after the first contact date",
                                            }}
                                            lessThan={{
                                                value: new Date(),
                                                errorMessage: "Date of last contact cannot be in the future",
                                            }}
                                            acceptDateFormatsErrorMsg={Contents.AcceptDateFormatsErrorMsg}
                                        />
                                    );
                                }
                            },
                        },
                        {
                            title: "Date Resolved",
                            key: "DateResolved",
                            render: (data, row: CustomerCompliance, index) => {
                                if (readOnly) {
                                    return row.DateResolved
                                        ? JsonDateParser.getFormatDateString(
                                              JsonDateParser.toDate(row.DateResolved),
                                              "dd-MMM-yyyy",
                                              true,
                                              true
                                          )
                                        : null;
                                } else {
                                    return (
                                        <DatePicker
                                            cssWrapper={"mb-0"}
                                            labelSRSufix="dd/mm/yyyy"
                                            selected={JsonDateParser.toDateNoOffset(row.DateResolved)}
                                            showYearDropdown={true}
                                            alwaysValidateOnUpdate={true}
                                            onChange={(date) => {
                                                customerComplianceList[
                                                    index
                                                ].DateResolved = JsonDateParser.toJsonDateNoOffset(date);
                                                this.setState({
                                                    customerComplianceList: customerComplianceList,
                                                });
                                            }}
                                            todayButton="Today"
                                            isClearable={false}
                                            dateFormat={"dd/MM/yyyy"}
                                            acceptDateFormats={[
                                                "dd/MM/yyyy",
                                                "dd-MM-yyyy",
                                                "ddMMyyyy",
                                                "dd.MM.yyyy",
                                                "dd_MM_yyyy",
                                                "dd MM yyyy",
                                            ]}
                                            atLeast={{
                                                value: JsonDateParser.toDateNoOffset(row.DateLastContacted),
                                                errorMessage:
                                                    "Date of resolved must be the same day or after last contact date",
                                            }}
                                            lessThan={{
                                                value: new Date(),
                                                errorMessage: "Date of resolved cannot be in the future",
                                            }}
                                            acceptDateFormatsErrorMsg={Contents.AcceptDateFormatsErrorMsg}
                                        />
                                    );
                                }
                            },
                        },
                        {
                            title: "Self Certification Received?",
                            key: "IsSelfCertificationReceived",
                            render: (data, row: CustomerCompliance, index) => {
                                if (readOnly) {
                                    return row?.IsSelfCertificationReceived ? "Yes" : "No";
                                } else {
                                    return (
                                        <CheckboxEditor
                                            value={row?.IsSelfCertificationReceived}
                                            cssWrapper="control-size-60 square-checkbox"
                                            label={""}
                                            onChange={(v) => {
                                                customerComplianceList[
                                                    index
                                                ].IsSelfCertificationReceived = !row.IsSelfCertificationReceived;
                                                this.setState({ customerComplianceList });
                                            }}
                                        />
                                    );
                                }
                            },
                        },
                    ]}
                />
                <div>
                    <div className="row row-center">
                        {this.props.hasReadWriteAccess && readOnly && (
                            <div className="col-auto">
                                <button
                                    className="edit-circle-icon-button label-sr-only"
                                    title="Edit"
                                    onClick={(val) => this.setState({ readOnly: false })}
                                >
                                    <span className="icon" aria-hidden="true"></span>
                                    <span className="text">Edit</span>
                                </button>
                            </div>
                        )}
                        {!readOnly && (
                            <div className="col-auto">
                                <button className="button mr-20" onClick={(val) => this.cancelEdit()}>
                                    Cancel
                                </button>
                            </div>
                        )}
                        {!readOnly && (
                            <div className="col-auto">
                                <button className="button" onClick={(val) => this.canShowLightboxForSubmit()}>
                                    Save
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                {this.state.showLightbox && (
                    <Lightbox
                        show={this.state.showLightbox}
                        title={"Regulatory"}
                        contentAfterTitle={null}
                        onClose={() => {}}
                        type={LightboxType.Modal}
                        unclosable={true}
                        noOverlay={false}
                        noAutofocus={true}
                        cssWrapper="is-default-lightbox is-narrow"
                    >
                        <div className="row row-center">
                            This update will be reflected on all of the following accounts:
                            {this.state.accountRoles.map((x, index) => {
                                return (
                                    <div
                                        key={x.ClAccountId}
                                        className="row row-center mr-17"
                                        id={index + x.ClAccountId}
                                    >
                                        {x.ClAccountId}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="row row-center">
                            <button
                                id="cancelBtn"
                                className="btn primary"
                                onClick={() => this.setState({ showLightbox: false })}
                            >
                                Cancel
                            </button>
                            <button
                                id="submitBtn"
                                className="btn primary ml-11"
                                onClick={() => this.saveCustomerCompliance()}
                            >
                                Submit
                            </button>
                        </div>
                    </Lightbox>
                )}
            </>
        );
    }

    private canShowLightboxForSubmit() {
        if (this.isValid() && this.table.isValid()) {
            this.setState({ showLightbox: true });
        }
    }

    private cancelEdit() {
        this.setState({ readOnly: true });
        this.getData();
    }

    private saveCustomerCompliance() {
        if (this.isValid() && this.table.isValid()) {
            const { customerComplianceList } = this.state;
            customerComplianceList.map((item: CustomerCompliance, index) => {
                if (item.CustomerRegulationComplianceId > 0) {
                    AccountInfoAction.updateCustomerRegulationCompliance(item, (callBack) => {
                        this.getData();
                    });
                } else {
                    item.DateTimeAtStatus = JsonDateParser.toJsonDateNoOffset(new Date());
                    AccountInfoAction.createCustomerRegulationCompliance(item);
                }
            });
        }
        this.setState({ readOnly: true, showLightbox: false });
    }
}
